/**
 * Message Content Types - STRONG TYPING
 *
 * Single source of truth for all message content variants.
 * NO `any` types, NO loose unions - every variant is explicitly typed.
 *
 * Author: Roronoa Zoro (Backend Swordsman)
 * THREE-SWORD STYLE: PRECISE TYPE STRIKES
 */

// ============================================================================
// BASE CONTENT TYPES
// ============================================================================

/**
 * Base text content - foundation for all message types
 * Every message has text, some have additional structured data
 */
export interface BaseTextContent {
  text: string;
  images?: string[];
}

// ============================================================================
// TOOL CONTENT TYPES
// ============================================================================

/**
 * Tool execution output
 * Structured result from an AI tool execution
 */
export interface ToolOutput {
  /** Unique tool identifier */
  toolId: string;
  /** Tool name */
  toolName: string;
  /** Execution result data */
  output: unknown;
  /** Whether execution succeeded */
  success: boolean;
  /** Error message if failed */
  error?: string;
  /** Time taken to execute (ms) */
  executionTime?: number;
}

/**
 * Tool call made by AI
 * Represents a request to execute a tool
 */
export interface ToolCall {
  /** Unique call identifier */
  id: string;
  /** Type of tool call */
  type: string;
  /** Function to execute */
  function: {
    /** Function name */
    name: string;
    /** Serialized JSON arguments */
    arguments: string;
  };
}

/**
 * Tool output content in messages
 * Contains results from AI tool executions
 */
export interface ToolOutputContent extends BaseTextContent {
  /** List of tool execution results */
  tools: ToolOutput[];
  /** Original tool calls that triggered these outputs */
  toolCalls?: ToolCall[];
}

// ============================================================================
// WORKFLOW CONTENT TYPES
// ============================================================================

/**
 * Workflow execution status
 * Extended from WorkflowExecutionStatus enum with additional message-specific states
 */
export type WorkflowExecutionStatusType =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"
  | "PAUSED"
  | "TIMEOUT";

/**
 * Workflow execution data
 * Represents a workflow run in message content
 */
export interface WorkflowExecution {
  /** Execution ID */
  id: string;
  /** Workflow template ID */
  workflowId: string;
  /** Workflow name */
  name: string;
  /** Current execution status */
  status: WorkflowExecutionStatusType;
  /** Execution start time */
  startedAt: string;
  /** Execution end time */
  completedAt?: string;
  /** Execution result data */
  result?: unknown;
  /** Error message if failed */
  error?: string;
}

/**
 * Workflow content in messages
 * Contains workflow execution information
 */
export interface WorkflowContent extends BaseTextContent {
  /** List of workflow executions */
  workflows: WorkflowExecution[];
  /** Direct workflow ID (backward compatibility) */
  workflowId?: string;
  /** Direct status (backward compatibility) */
  status?: WorkflowExecutionStatusType;
}

// ============================================================================
// FILE CONTENT TYPES
// ============================================================================

/**
 * File attachment metadata
 */
export interface FileAttachment {
  /** File ID */
  id: string;
  /** File name */
  name: string;
  /** MIME type */
  type: string;
  /** File size in bytes */
  size: number;
  /** Download URL */
  url: string;
  /** Upload timestamp */
  uploadedAt: string;
  /** Extracted text content (if applicable) */
  textContent?: string;
}

/**
 * File content in messages
 * Contains attached files
 */
export interface FileContent extends BaseTextContent {
  /** List of attached files */
  files: FileAttachment[];
}

// ============================================================================
// KNOWLEDGE SOURCE TYPES
// ============================================================================

/**
 * Knowledge source citation
 * Reference to knowledge used in AI response
 */
export interface KnowledgeSource {
  /** Source ID */
  id: string;
  /** Source title */
  title: string;
  /** Source type */
  type: "DOCUMENT" | "URL" | "DATABASE" | "API" | "OTHER";
  /** Source URL or path */
  url?: string;
  /** Relevance score (0-1) */
  relevance?: number;
  /** Excerpt from source */
  excerpt?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Source/knowledge citation content
 * Contains references to knowledge sources used
 */
export interface SourceContent extends BaseTextContent {
  /** List of knowledge sources */
  sources: KnowledgeSource[];
}

// ============================================================================
// HTML TEMPLATE TYPES
// ============================================================================

/**
 * Rendered HTML message content (NEX-2541).
 * `htmlTemplate.html` is server-sanitized HTML — rendered from a stored
 * HtmlMessageTemplate, from the embed `send_message` card tool, or from the
 * embed `display_html` tool. `text` carries the plaintext fallback that
 * non-HTML surfaces (and the LLM transcript) render instead.
 */
export interface HtmlTemplateContent extends BaseTextContent {
  htmlTemplate: {
    /** Sanitized, rendered HTML (no scripts / event handlers / iframes). */
    html: string;
    /** The HtmlMessageTemplate this was rendered from (absent for ad-hoc cards). */
    templateId?: string;
    templateName?: string;
  };
}

// ============================================================================
// DISCRIMINATED UNION
// ============================================================================

/**
 * Discriminated union of all possible message content types
 *
 * Use this for type-safe message content handling:
 *
 * ```typescript
 * if ('tools' in content && content.tools.length > 0) {
 *   // TypeScript knows content is ToolOutputContent
 *   console.log(content.tools[0].toolName);
 * }
 * ```
 */
export type MessageContent =
  | BaseTextContent
  | ToolOutputContent
  | WorkflowContent
  | FileContent
  | SourceContent
  | HtmlTemplateContent;

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard: Check if content has tool outputs
 */
export function hasToolOutput(content: MessageContent): content is ToolOutputContent {
  return "tools" in content && Array.isArray(content.tools) && content.tools.length > 0;
}

/**
 * Type guard: Check if content has workflows
 */
export function hasWorkflow(content: MessageContent): content is WorkflowContent {
  return "workflows" in content && Array.isArray(content.workflows) && content.workflows.length > 0;
}

/**
 * Type guard: Check if content has files
 */
export function hasFiles(content: MessageContent): content is FileContent {
  return "files" in content && Array.isArray(content.files) && content.files.length > 0;
}

/**
 * Type guard: Check if content has knowledge sources
 */
export function hasSources(content: MessageContent): content is SourceContent {
  return "sources" in content && Array.isArray(content.sources) && content.sources.length > 0;
}

/**
 * Type guard: Check if content carries a rendered HTML template
 */
export function hasHtmlTemplate(content: MessageContent): content is HtmlTemplateContent {
  return (
    "htmlTemplate" in content &&
    typeof content.htmlTemplate === "object" &&
    content.htmlTemplate !== null &&
    typeof (content.htmlTemplate as { html?: unknown }).html === "string" &&
    (content.htmlTemplate as { html: string }).html.length > 0
  );
}

/**
 * Type guard: Check if content is plain text only
 */
export function isPlainText(content: MessageContent): content is BaseTextContent {
  return (
    "text" in content &&
    !hasToolOutput(content) &&
    !hasWorkflow(content) &&
    !hasFiles(content) &&
    !hasSources(content) &&
    !hasHtmlTemplate(content)
  );
}

// ============================================================================
// NORMALIZATION UTILITIES
// ============================================================================

/**
 * Normalize any content to proper MessageContent structure
 * Handles legacy string format and unknown types
 *
 * @param content - Any message content (string, object, or MessageContent)
 * @returns Normalized MessageContent (at minimum BaseTextContent)
 */
export function normalizeMessageContent(content: unknown): MessageContent {
  // Already properly typed?
  if (content && typeof content === "object" && "text" in content) {
    return content as MessageContent;
  }

  // Legacy string format
  if (typeof content === "string") {
    return { text: content };
  }

  // Invalid - return empty
  return { text: "" };
}

/**
 * Extract plain text from any MessageContent
 * Handles all content type variants
 *
 * @param content - Any MessageContent variant
 * @returns The text field, or empty string if not found
 */
export function extractText(content: MessageContent | null | undefined): string {
  if (!content) return "";
  return content.text || "";
}

/**
 * Extract images from any MessageContent
 *
 * @param content - Any MessageContent variant
 * @returns Array of image URLs, or empty array
 */
export function extractImages(content: MessageContent | null | undefined): string[] {
  if (!content || !content.images) return [];
  return content.images;
}
